/**
 * Servicio para la gestión de materias primas (sistema unificado vía productos).
 *
 * Lectura: GET /materias-primas-unificadas
 * CRUD: API /productos con tipo materia_prima
 *
 * @module services/materiasPrimas.service
 */

import FirebaseService from './firebase.service';
import ApiService from './api.service';
import categoriasService from './categorias.service';

class MateriasPrimasService extends FirebaseService {
  constructor() {
    super('/productos');
    this.compatApi = new ApiService('');
  }

  async _fetchUnificadas() {
    const response = await this.compatApi.get('/materias-primas-unificadas');
    const payload = response?.data?.data ?? response?.data;
    return this.ensureArray(payload);
  }

  async _resolveCategoriaMPId() {
    if (this._categoriaMPId) return this._categoriaMPId;
    try {
      const categorias = await categoriasService.obtenerTodas();
      const list = Array.isArray(categorias) ? categorias : categorias?.data || [];
      const categoriaMP = list.find((c) => c.nombre === 'Materias Primas');
      if (categoriaMP?.id) {
        this._categoriaMPId = String(categoriaMP.id);
        return this._categoriaMPId;
      }
    } catch (error) {
      console.warn('No se pudo resolver categoría Materias Primas:', error.message);
    }
    return null;
  }

  async _mapToProducto(materiaPrima) {
    const precioUnitario = parseFloat(materiaPrima.precio_unitario ?? materiaPrima.precio_costo ?? 0);
    const categoriaId = materiaPrima.categoria_id || (await this._resolveCategoriaMPId()) || '';
    return {
      codigo: materiaPrima.codigo || '',
      nombre: materiaPrima.nombre,
      descripcion: materiaPrima.descripcion || '',
      unidad_medida: materiaPrima.unidad_medida || 'unidad',
      precio_costo: precioUnitario,
      precio_venta: precioUnitario,
      stock_actual: parseFloat(materiaPrima.stock_actual || 0),
      stock_minimo: parseFloat(materiaPrima.stock_minimo || 0),
      proveedor_id: materiaPrima.proveedor_id || '',
      categoria_id: categoriaId,
      activo: materiaPrima.activo !== false,
      tipo: 'materia_prima',
      es_materia_prima: true
    };
  }

  async obtenerTodas() {
    try {
      return await this._fetchUnificadas();
    } catch (error) {
      console.error('Error al obtener materias primas:', error);
      throw error;
    }
  }

  async obtenerActivas() {
    const all = await this.obtenerTodas();
    return all.filter((m) => m.activo !== false);
  }

  async obtenerStockBajo() {
    const all = await this.obtenerTodas();
    return all.filter(
      (m) => parseFloat(m.stock_actual || 0) <= parseFloat(m.stock_minimo || 5)
    );
  }

  async obtenerPorId(id) {
    try {
      const response = await this.get(`/${id}`);
      return this.ensureObject(response);
    } catch (error) {
      const all = await this._fetchUnificadas();
      const found = all.find((m) => String(m.id) === String(id));
      if (found) return found;
      throw error;
    }
  }

  async crear(materiaPrima) {
    try {
      const data = await this._mapToProducto(materiaPrima);
      console.log('📤 Creando materia prima (producto unificado):', data);
      return await this.post('', data);
    } catch (error) {
      console.error('Error al crear materia prima:', error);
      throw error;
    }
  }

  async actualizar(id, materiaPrima) {
    try {
      const data = await this._mapToProducto(materiaPrima);
      console.log('📤 Actualizando materia prima (producto unificado):', data);
      return await this.put(`/${id}`, data);
    } catch (error) {
      console.error('Error al actualizar materia prima:', error);
      throw error;
    }
  }

  async eliminar(id) {
    try {
      return await this.delete(`/${id}`);
    } catch (error) {
      console.error('Error al eliminar materia prima:', error);
      throw error;
    }
  }

  async buscar(termino) {
    try {
      const all = await this.obtenerTodas();
      const q = String(termino || '').trim().toLowerCase();
      if (!q) return all;
      return all.filter((m) => {
        const nombre = String(m.nombre || '').toLowerCase();
        const codigo = String(m.codigo || '').toLowerCase();
        return nombre.includes(q) || codigo.includes(q);
      });
    } catch (error) {
      console.error('Error al buscar materias primas:', error);
      throw error;
    }
  }

  async actualizarStock(id, cantidad) {
    return this.put(`/${id}`, {
      stock_actual: parseFloat(cantidad),
      tipo: 'materia_prima'
    });
  }

  async ajustarStock(id, datos, motivo = '') {
    try {
      if (typeof datos === 'number' || typeof datos === 'string') {
        const actual = await this.obtenerPorId(id);
        const stockActual = parseFloat(actual.stock_actual || 0);
        const ajuste = parseFloat(datos);
        return this.put(`/${id}`, {
          stock_actual: stockActual + ajuste,
          tipo: 'materia_prima'
        });
      }

      return this.put(`/${id}`, {
        stock_actual: parseFloat(datos.stock_actual || 0),
        tipo: 'materia_prima'
      });
    } catch (error) {
      console.error('Error al ajustar stock:', error);
      throw error;
    }
  }

  async obtenerMovimientos(id) {
    try {
      const stockService = new FirebaseService('/stock-sucursal');
      const movimientos = await stockService.get(`/producto/${id}/movimientos`);
      return this.ensureArray(movimientos);
    } catch (error) {
      console.error('Error al obtener movimientos:', error);
      return [];
    }
  }

  async obtenerPorProveedor(proveedorId) {
    const all = await this.obtenerTodas();
    return all.filter((m) => String(m.proveedor_id) === String(proveedorId));
  }
}

const materiasPrimasService = new MateriasPrimasService();
export default materiasPrimasService;
